// SPDX-License-Identifier: AGPL-3.0-or-later
import {getDateFromUnixTimestampSeconds} from '@app/features/messaging/utils/markdown/TimestampValidation';
import {getRelativeDayLabelCapitalized, getRelativeTimeFormat} from '@app/features/ui/utils/RelativeDayLabels';
import {shouldUse12HourFormat} from '@app/features/user/utils/dateFormatting';
import {getCurrentLocale} from '@app/features/user/utils/LocaleUtils';
import {
	getDaysDiff,
	getHoursDiff,
	getMinutesDiff,
	getSecondsDiff,
	isSameDay,
	isYesterday,
} from '@app/vendor/date_utils/DateComparison';
import {getFormattedTime} from '@app/vendor/date_utils/DateFormatting';
import {formatTimestampWithStyle} from '@app/vendor/date_utils/DateTimestampStyle';
import {TimestampStyle} from '@app/vendor/markdown_parser/types/Enums';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

let cachedNow: Date | null = null;
let cachedNowExpiry = 0;
function getSharedNow(): Date {
	const t = Date.now();
	if (cachedNow == null || t >= cachedNowExpiry) {
		cachedNow = new Date(t);
		cachedNowExpiry = t + 250;
	}
	return cachedNow;
}
function formatRelativeTime(date: Date, i18n: I18n): string {
	const locale = getCurrentLocale();
	const now = getSharedNow();
	const hour12 = shouldUse12HourFormat(locale);
	const rtf = getRelativeTimeFormat(locale);
	if (isSameDay(date, now)) {
		const timeString = getFormattedTime(date, locale, hour12);
		const todayLabel = getRelativeDayLabelCapitalized(locale, 0);
		return i18n._(msg`${todayLabel} at ${timeString}`);
	}
	if (isYesterday(date, now)) {
		const timeString = getFormattedTime(date, locale, hour12);
		const yesterdayLabel = getRelativeDayLabelCapitalized(locale, -1);
		return i18n._(msg`${yesterdayLabel} at ${timeString}`);
	}
	const daysDiff = getDaysDiff(date, now);
	const absDays = Math.abs(daysDiff);
	if (absDays >= 365) {
		const years = Math.floor(absDays / 365);
		return rtf.format(date > now ? years : -years, 'year');
	}
	if (absDays >= 30) {
		const months = Math.floor(absDays / 30);
		return rtf.format(date > now ? months : -months, 'month');
	}
	if (absDays >= 7) {
		const weeks = Math.floor(absDays / 7);
		return rtf.format(date > now ? weeks : -weeks, 'week');
	}
	if (absDays > 0) {
		return rtf.format(date > now ? absDays : -absDays, 'day');
	}
	const hoursDiff = getHoursDiff(date, now);
	const absHours = Math.abs(hoursDiff);
	if (absHours > 0) {
		return rtf.format(date > now ? absHours : -absHours, 'hour');
	}
	const minutesDiff = getMinutesDiff(date, now);
	const absMinutes = Math.abs(minutesDiff);
	if (absMinutes > 0) {
		return rtf.format(date > now ? absMinutes : -absMinutes, 'minute');
	}
	const secondsDiff = getSecondsDiff(date, now);
	const absSeconds = Math.abs(secondsDiff);
	return rtf.format(date > now ? absSeconds : -absSeconds, 'second');
}
export function formatTimestamp(timestamp: number, style: TimestampStyle, i18n: I18n): string {
	const locale = getCurrentLocale();
	const hour12 = shouldUse12HourFormat(locale);
	const date = getDateFromUnixTimestampSeconds(timestamp);
	if (date == null) {
		return String(timestamp);
	}
	if (style === TimestampStyle.RelativeTime) {
		return formatRelativeTime(date, i18n);
	}
	return formatTimestampWithStyle(timestamp, style, locale, hour12);
}
